import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  useMediaQuery, 
  LinearProgress,
  Chip,
  Tooltip,
  Zoom,
  Fade,
  Container,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUsers,
  FaStar,
  FaTasks,
  FaCheckCircle,
  FaClock,
  FaChartLine,
  FaLeaf,
  FaRecycle as FaRecycleIcon,
} from "react-icons/fa";
import {
  GiRecycle,
} from "react-icons/gi";
import {
  MdOutlineReportProblem,
} from "react-icons/md";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const MotionPaper = motion(Paper);

const DashboardStats = ({ stats }) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [animateCards, setAnimateCards] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:1200px)");

  const { all_reports, all_issues, all_reviews, all_users } = stats || {};

  // Calculate percentages
  const totalIssues = all_issues?.total || 1;
  const resolvedPercent = ((all_issues?.resolved || 0) / totalIssues * 100).toFixed(1);
  const inProgressPercent = ((all_issues?.in_progress || 0) / totalIssues * 100).toFixed(1);
  const pendingPercent = ((all_issues?.pending || 0) / totalIssues * 100).toFixed(1);

  // Status distribution data for pie chart - Only 3 statuses
  const statusData = [
    { name: 'Resolved', value: all_issues?.resolved || 0, color: '#43a047' },
    { name: 'In Progress', value: all_issues?.in_progress || 0, color: '#ff9800' },
    { name: 'Pending', value: all_issues?.pending || 0, color: '#f44336' },
  ];

  const statItems = [
    { 
      label: "Environmental Reports", 
      value: all_reports?.total || 0, 
      color: "#2e7d32", 
      icon: <FaLeaf size={isMobile ? 24 : 28} color="#2e7d32" />,
      subtitle: `All reports made`,
      gradient: "linear-gradient(135deg, #e8f5e9, #c8e6c9)",
      delay: 0.1,
    },
    { 
      label: "Active Issues", 
      value: all_issues?.total || 0, 
      color: "#0277bd", 
      icon: <MdOutlineReportProblem size={isMobile ? 24 : 28} color="#0277bd" />,
      subtitle: `Total number of issues`,
      gradient: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
      delay: 0.15,
    },
    { 
      label: "Resolved Issues", 
      value: all_issues?.resolved || 0, 
      color: "#43a047", 
      icon: <FaCheckCircle size={isMobile ? 24 : 28} color="#43a047" />,
      subtitle: `${resolvedPercent}% resolution rate`,
      gradient: "linear-gradient(135deg, #e8f5e9, #a5d6a7)",
      delay: 0.2,
    },
    { 
      label: "In Progress", 
      value: all_issues?.in_progress || 0, 
      color: "#f57c00", 
      icon: <FaTasks size={isMobile ? 24 : 28} color="#f57c00" />,
      subtitle: `${inProgressPercent}% active`,
      gradient: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
      delay: 0.25,
    },
    { 
      label: "Community Reviews", 
      value: all_reviews?.average?.toFixed(1) || 0, 
      color: "#f9a825", 
      icon: <FaStar size={isMobile ? 24 : 28} color="#f9a825" />,
      subtitle: `${all_reviews?.total || 0} total reviews`,
      gradient: "linear-gradient(135deg, #fff8e1, #ffecb3)",
      delay: 0.3,
    },
    { 
      label: "Eco-Community", 
      value: all_users?.total || 0, 
      color: "#1b5e20", 
      icon: <FaUsers size={isMobile ? 24 : 28} color="#1b5e20" />,
      subtitle: `${all_users?.active || 0} active members`,
      gradient: "linear-gradient(135deg, #e8f5e9, #a5d6a7)",
      delay: 0.35,
    },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ 
          p: 2, 
          bgcolor: 'rgba(0,40,15,0.95)', 
          color: '#a5d6a7',
          borderRadius: '12px',
          border: '1px solid rgba(165,214,167,0.2)',
          backdropFilter: 'blur(10px)',
        }}>
          <Typography variant="body2" fontWeight={600}>{payload[0].name}</Typography>
          <Typography variant="h6" sx={{ color: '#fff' }}>{payload[0].value}</Typography>
        </Paper>
      );
    }
    return null;
  };

  if (!stats) return null;

  return (
    <Box mb={6}>
      {/* Stats Cards Grid */}
      <Grid container spacing={isMobile ? 2 : 3} justifyContent="center">
        {statItems.map((stat, i) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
            <Zoom 
              in={true} 
              style={{ transitionDelay: `${i * 50}ms` }}
              timeout={500}
            >
              <Paper
                elevation={hoveredCard === i ? 8 : 2}
                onMouseEnter={() => setHoveredCard(i)}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{
                  p: isMobile ? 2 : 3,
                  borderRadius: "20px",
                  textAlign: "center",
                  background: stat.gradient,
                  height: "100%",
                  minHeight: isMobile ? 140 : 180,
                  boxShadow: hoveredCard === i
                    ? `0 12px 40px ${stat.color}40`
                    : "0 4px 12px rgba(0,0,0,0.06)",
                  transform: hoveredCard === i ? "translateY(-6px)" : "translateY(0)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  position: "relative",
                  overflow: "hidden",
                  cursor: "pointer",
                  border: `1px solid ${stat.color}20`,
                }}
              >
                {/* Decorative Background Elements */}
                <Box 
                  sx={{
                    position: "absolute",
                    top: -30,
                    right: -30,
                    width: isMobile ? 80 : 120,
                    height: isMobile ? 80 : 120,
                    borderRadius: "50%",
                    background: `${stat.color}10`,
                    pointerEvents: 'none',
                  }}
                />
                <Box 
                  sx={{
                    position: "absolute",
                    bottom: -20,
                    left: -20,
                    width: isMobile ? 50 : 80,
                    height: isMobile ? 50 : 80,
                    borderRadius: "50%",
                    background: `${stat.color}06`,
                    pointerEvents: 'none',
                  }}
                />
                
                {/* Content */}
                <Box position="relative" zIndex={1}>
                  <Box mb={1.5}>{stat.icon}</Box>
                  <Typography 
                    variant={isMobile ? "h4" : "h3"} 
                    fontWeight="bold" 
                    sx={{ 
                      color: stat.color, 
                      mb: 0.5,
                      fontSize: isMobile ? '1.8rem' : isTablet ? '2.2rem' : '2.8rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight={600} 
                    color="text.primary"
                    sx={{
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      fontSize: isMobile ? '0.65rem' : '0.75rem',
                    }}
                  >
                    {stat.subtitle}
                  </Typography>
                  
                  {/* Progress Bar on Hover */}
                  {hoveredCard === i && (
                    <Fade in timeout={300}>
                      <Box sx={{ mt: 1.5 }}>
                        <LinearProgress
                          variant="determinate"
                          value={i === 0 ? (all_reports?.new / (all_reports?.total || 1) * 100) : 
                                 i === 1 ? (all_issues?.new / totalIssues * 100) :
                                 i === 2 ? resolvedPercent : 
                                 i === 3 ? inProgressPercent : 75}
                          sx={{
                            height: 4,
                            borderRadius: 2,
                            bgcolor: `${stat.color}20`,
                            '& .MuiLinearProgress-bar': {
                              bgcolor: stat.color,
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Box>
                    </Fade>
                  )}
                </Box>
              </Paper>
            </Zoom>
          </Grid>
        ))}
      </Grid>

      {/* Charts Section - Responsive */}
      <Box sx={{ mt: 4 }}>
        <Grid container spacing={isMobile ? 2 : 4} justifyContent="center">
          {/* Pie Chart - Issue Status Distribution */}
          <Grid item xs={12} md={8} lg={6}>
            <Paper
              elevation={3}
              sx={{
                p: isMobile ? 2 : 4,
                borderRadius: "20px",
                background: "linear-gradient(135deg, #ffffff, #f5faf6)",
                height: "100%",
                minHeight: isMobile ? 350 : 420,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                fontWeight={600} 
                color="#2e7d32" 
                gutterBottom 
                sx={{ textAlign: "center" }}
              >
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <FaChartLine size={isMobile ? 16 : 20} color="#2e7d32" />
                  Issue Status Distribution
                </Box>
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 380}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="45%"
                    innerRadius={isMobile ? 60 : 80}
                    outerRadius={isMobile ? 100 : 130}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => isMobile ? `${(percent * 100).toFixed(0)}%` : `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={!isMobile}
                  >
                    {statusData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.color}
                        style={{
                          filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))',
                          cursor: 'pointer',
                        }}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={isMobile ? 30 : 40}
                    formatter={(value) => (
                      <span style={{ 
                        color: '#1a1a2e', 
                        fontWeight: 500, 
                        fontSize: isMobile ? '0.7rem' : '0.9rem' 
                      }}>
                        {value}
                      </span>
                    )}
                    wrapperStyle={{
                      fontSize: isMobile ? '0.7rem' : '0.9rem',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Status Overview Cards */}
          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                p: isMobile ? 2 : 3,
                borderRadius: "20px",
                background: "linear-gradient(135deg, #ffffff, #f5faf6)",
              }}
            >
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                fontWeight={600} 
                color="#2e7d32" 
                gutterBottom 
                sx={{ textAlign: "center" }}
              >
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <GiRecycle size={isMobile ? 20 : 24} color="#2e7d32" />
                  Environmental Issue Status Overview
                </Box>
              </Typography>
              
              <Grid container spacing={isMobile ? 2 : 3} justifyContent="center">
                {/* Resolved */}
                <Grid item xs={12} sm={4}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: isMobile ? 2 : 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(67,160,71,0.2)',
                    },
                  }}>
                    <Typography variant={isMobile ? "h5" : "h4"} color="#43a047" fontWeight="bold">
                      {resolvedPercent}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      Resolved Rate
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={resolvedPercent}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        mt: 2,
                        bgcolor: 'rgba(255,255,255,0.5)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#43a047',
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Chip
                      icon={<FaCheckCircle size={14} />}
                      label={`${all_issues?.resolved || 0} resolved`}
                      size="small"
                      sx={{
                        mt: 1.5,
                        bgcolor: '#43a047',
                        color: '#fff',
                        '& .MuiChip-icon': { color: '#fff' },
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Grid>

                {/* In Progress */}
                <Grid item xs={12} sm={4}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: isMobile ? 2 : 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #fff3e0, #ffe0b2)',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(245,124,0,0.2)',
                    },
                  }}>
                    <Typography variant={isMobile ? "h5" : "h4"} color="#f57c00" fontWeight="bold">
                      {inProgressPercent}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      In Progress
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={inProgressPercent}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        mt: 2,
                        bgcolor: 'rgba(255,255,255,0.5)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#f57c00',
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Chip
                      icon={<FaTasks size={14} />}
                      label={`${all_issues?.in_progress || 0} active`}
                      size="small"
                      sx={{
                        mt: 1.5,
                        bgcolor: '#f57c00',
                        color: '#fff',
                        '& .MuiChip-icon': { color: '#fff' },
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Grid>

                {/* Pending */}
                <Grid item xs={12} sm={4}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: isMobile ? 2 : 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #ffebee, #ffcdd2)',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(244,67,54,0.2)',
                    },
                  }}>
                    <Typography variant={isMobile ? "h5" : "h4"} color="#f44336" fontWeight="bold">
                      {pendingPercent}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      Pending
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={pendingPercent}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        mt: 2,
                        bgcolor: 'rgba(255,255,255,0.5)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#f44336',
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Chip
                      icon={<FaClock size={14} />}
                      label={`${all_issues?.pending || 0} pending`}
                      size="small"
                      sx={{
                        mt: 1.5,
                        bgcolor: '#f44336',
                        color: '#fff',
                        '& .MuiChip-icon': { color: '#fff' },
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Environmental Impact Message */}
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: isMobile ? 2 : 3,
                  borderRadius: "16px",
                  background: "linear-gradient(135deg, #e8f5e9, #a5d6a7)",
                  textAlign: "center",
                }}
              >
                <Typography 
                  variant={isMobile ? "body2" : "body1"} 
                  fontWeight={600} 
                  color="#1b5e20"
                >
                  🌿 Together, we've reported <strong>{all_reports?.total || 0}</strong> environmental issues, 
                  with <strong>{all_issues?.resolved || 0}</strong> resolved and <strong>{all_issues?.in_progress || 0}</strong> in progress. 
                  Keep making a difference!
                </Typography>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default DashboardStats;